import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import Nav from "../../components/breadcrumb";
import { ErrorLoading, PageLoading } from "../../components/loading";
import PageHeader, { ActionBar, ActionButton, Title } from "../../components/pageheader";
import { getEvents } from "../../resources/api/events";
import EventForm, { emptyEvent } from "./event-form";

const bool = value => value === true || value === 1 || value === "1";
const Status = ({ open, children }) => <Badge bg={open ? "success" : "secondary"}>{children} {open ? "Open" : "Closed"}</Badge>;

const ViewEvents = () => {
    const [events, setEvents] = useState([]);
    const [search, setSearch] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [selected, setSelected] = useState(emptyEvent);

    useEffect(() => {
        getEvents()
            .then(({ events }) => setEvents(events || []))
            .catch(setError)
            .finally(() => setLoaded(true));
    }, []);

    const filtered = useMemo(() => {
        const value = search.trim().toLowerCase();
        if (!value) return events;
        return events.filter(event => [event.title, event.location, event.short_code, event.status]
            .some(field => String(field || "").toLowerCase().includes(value)));
    }, [events, search]);

    const create = () => {
        setSelected(emptyEvent);
        setShowForm(true);
    };

    const onSave = event => setEvents(items => {
        const index = items.findIndex(item => item.id === event.id);
        if (index === -1) return [event, ...items];
        const copy = [...items];
        copy.splice(index, 1, event);
        return copy;
    });

    if (!loaded) return <PageLoading>Loading events...</PageLoading>;
    if (error) return <ErrorLoading>{error}</ErrorLoading>;

    return (
        <>
            <Nav items={[{ title: "Events" }]} />
            <PageHeader maxWidth="1000">
                <Title>Events</Title>
                <ActionBar>
                    <ActionButton onClick={create}><i className="fas fa-plus-circle me-1" />New Event</ActionButton>
                </ActionBar>
            </PageHeader>
            <div className="max-1000">
                <Row className="mb-3 justify-content-end">
                    <Col sm={6} md={4}>
                        <Form.Control size="sm" name="search" placeholder="Search events" value={search} onChange={e => setSearch(e.currentTarget.value)} />
                    </Col>
                </Row>

                {filtered.length === 0 ? (
                    <Card body className="text-center text-muted py-5">
                        <i className="far fa-calendar fa-3x mb-3" />
                        <p className="mb-3">{events.length ? "No events match your search." : "No events have been created yet."}</p>
                        {!events.length && <Button variant="success" size="sm" onClick={create}>Create the first event</Button>}
                    </Card>
                ) : (
                    <Table responsive hover style={{ minWidth: 760 }}>
                        <thead><tr><th>Event</th><th>Date</th><th>Location</th><th>Code</th><th>Registration</th><th>Feedback</th><th>Status</th></tr></thead>
                        <tbody>{filtered.map(event => (
                            <tr key={event.id}>
                                <td><Link to={`/app/events/${event.id}`}>{event.title}</Link></td>
                                <td>{event.start_date ? moment(event.start_date).format("DD MMM YYYY") : "n/a"}</td>
                                <td>{event.location || "n/a"}</td>
                                <td><code>{event.short_code || "Pending"}</code></td>
                                <td><Status open={bool(event.registrationOpen)}>Registration</Status></td>
                                <td><Status open={bool(event.feedbackOpen)}>Feedback</Status></td>
                                <td className="text-capitalize">{event.status}</td>
                            </tr>
                        ))}</tbody>
                    </Table>
                )}
            </div>
            <EventForm show={showForm} setShow={setShowForm} event={selected} onSave={onSave} />
        </>
    );
};

export default ViewEvents;
